import leagueConfig from '../config/footballLeagues.json';

/**
 * Football League Utility Functions
 * Provides helper methods for working with football league configuration
 */

/**
 * Get all countries with their leagues
 * @returns {Array} Array of country objects with leagues
 */
export const getAllCountries = () => {
  return leagueConfig.leagues;
};

/**
 * Get all leagues flattened into a single array
 * @returns {Array} Array of all leagues with country info
 */
export const getAllLeagues = () => {
  const allLeagues = [];
  
  leagueConfig.leagues.forEach(country => {
    country.leagues.forEach(league => {
      allLeagues.push({
        ...league,
        country: country.country,
        countryCode: country.countryCode,
        flag: country.flag
      });
    });
  });
  
  return allLeagues;
};

/**
 * Get leagues by country
 * @param {string} countryName - Country name
 * @returns {Array} Array of leagues for the country
 */
export const getLeaguesByCountry = (countryName) => {
  const country = leagueConfig.leagues.find(c => 
    c.country.toLowerCase() === countryName.toLowerCase()
  );
  
  return country ? country.leagues : [];
};

/**
 * Get league by ID
 * @param {number} leagueId - League ID
 * @returns {Object|null} League object or null if not found
 */
export const getLeagueById = (leagueId) => {
  for (const country of leagueConfig.leagues) {
    const league = country.leagues.find(l => l.id === leagueId);
    if (league) {
      return {
        ...league,
        country: country.country,
        countryCode: country.countryCode,
        flag: country.flag
      };
    }
  }
  return null;
};

/**
 * Get current season info for a league
 * @param {number} leagueId - League ID
 * @returns {Object|null} Season info or null if not found
 */
export const getCurrentSeasonInfo = (leagueId) => {
  return leagueConfig.currentSeasons[leagueId] || null;
};

/**
 * Get leagues by type
 * @param {string} type - League type (league, cup, continental, international, etc.)
 * @returns {Array} Array of leagues of the specified type
 */
export const getLeaguesByType = (type) => {
  const leagues = [];
  
  leagueConfig.leagues.forEach(country => {
    country.leagues.forEach(league => {
      if (league.type === type) {
        leagues.push({
          ...league,
          country: country.country,
          countryCode: country.countryCode,
          flag: country.flag
        });
      }
    });
  });
  
  return leagues;
};

/**
 * Get top priority leagues (priority 1)
 * @returns {Array} Array of top priority leagues
 */
export const getTopLeagues = () => {
  const leagues = [];
  
  leagueConfig.leagues.forEach(country => {
    country.leagues.forEach(league => {
      if (league.priority === 1) {
        leagues.push({
          ...league,
          country: country.country,
          countryCode: country.countryCode,
          flag: country.flag
        });
      }
    });
  });
  
  return leagues.sort((a, b) => {
    // Sort by specific order for popular leagues
    const order = [39, 140, 78, 135, 61, 2, 1, 71, 253, 262, 13];
    const aIndex = order.indexOf(a.id);
    const bIndex = order.indexOf(b.id);
    
    if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex;
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;
    
    return a.name.localeCompare(b.name);
  });
};

/**
 * Get countries for dropdown (unique list)
 * @returns {Array} Array of country objects for dropdown
 */
export const getCountriesForDropdown = () => {
  return leagueConfig.leagues.map(country => ({
    name: country.country,
    code: country.countryCode,
    flag: country.flag,
    leagueCount: country.leagues.length
  }));
};

/**
 * Get popular leagues (curated list)
 * @returns {Array} Array of popular leagues
 */
export const getPopularLeagues = () => {
  // Prioritize Brazilian leagues first for Brazilian users
  const popularIds = [
    71,  // Brazil Serie A (Brasileirão)
    73,  // Copa Do Brasil
    72,  // Brazil Serie B
    632, // Supercopa do Brasil
    75,  // Brazil Serie C
    76,  // Brazil Serie D
    39,  // Premier League
    140, // La Liga
    78,  // Bundesliga
    135, // Serie A (Italy)
    61,  // Ligue 1
    2,   // UEFA Champions League
    253, // MLS
    262, // Liga MX
    13,  // Copa Libertadores
    307  // Liga Portugal
  ];
  const leagues = [];
  
  popularIds.forEach(id => {
    const league = getLeagueById(id);
    if (league) leagues.push(league);
  });
  
  return leagues;
};

/**
 * Search leagues by name
 * @param {string} searchTerm - Search term
 * @returns {Array} Array of matching leagues
 */
export const searchLeagues = (searchTerm) => {
  const term = searchTerm.toLowerCase();
  const leagues = [];
  
  leagueConfig.leagues.forEach(country => {
    country.leagues.forEach(league => {
      // Check league name
      const nameMatch = league.name.toLowerCase().includes(term);
      
      // Check country name
      const countryMatch = country.country.toLowerCase().includes(term);
      
      // Check aliases if they exist
      const aliasMatch = league.aliases && 
        league.aliases.some(alias => alias.toLowerCase().includes(term));
      
      if (nameMatch || countryMatch || aliasMatch) {
        leagues.push({
          ...league,
          country: country.country,
          countryCode: country.countryCode,
          flag: country.flag
        });
      }
    });
  });
  
  return leagues;
};

/**
 * Group leagues by type
 * @returns {Object} Object with leagues grouped by type
 */
export const getLeaguesGroupedByType = () => {
  const grouped = {
    league: [],
    cup: [],
    continental: [],
    international: [],
    regional: [],
    youth: [],
    women: []
  };
  
  leagueConfig.leagues.forEach(country => {
    country.leagues.forEach(league => {
      const type = league.type || 'league';
      if (grouped[type]) {
        grouped[type].push({
          ...league,
          country: country.country,
          countryCode: country.countryCode,
          flag: country.flag
        });
      }
    });
  });
  
  return grouped;
};

/**
 * Get available seasons from current season data
 * @returns {Array} Array of unique season years
 */
export const getAvailableSeasons = () => {
  const seasons = new Set();
  
  Object.values(leagueConfig.currentSeasons).forEach(seasonInfo => {
    seasons.add(seasonInfo.season);
  });
  
  // Add past seasons
  const currentYear = new Date().getFullYear();
  for (let year = currentYear; year >= 2020; year--) {
    seasons.add(year.toString());
  }
  
  return Array.from(seasons).sort((a, b) => b - a);
};

/**
 * Format league display name with country
 * @param {Object} league - League object
 * @returns {string} Formatted display name
 */
export const formatLeagueDisplayName = (league) => {
  if (league.country === 'World') {
    return league.name;
  }
  return `${league.name} - ${league.country}`;
};

/**
 * Get league priority level description
 * @param {number} priority - Priority level (1-5)
 * @returns {string} Priority description
 */
export const getPriorityDescription = (priority) => {
  const descriptions = {
    1: 'Top Tier',
    2: 'Second Tier',
    3: 'Third Tier',
    4: 'Regional/Lower',
    5: 'Other'
  };
  
  return descriptions[priority] || 'Unknown';
};